import type EventBase from '@/model/EventClass/EventBase'
import EventID from '@/model/EventClass/EventID'
import { isFoldBlockHeader } from '@/model/EventClass/eventFromReader'

type StructureRule = {
  headers: EventID[]
  options: EventID[]
  ends: EventID[]
}

const STRUCTURE_RULES: readonly StructureRule[] = [
  {
    headers: [EventID.TextDif, EventID.TextDifEx, EventID.TextDifEx2],
    options: [EventID.TextChoice],
    ends: [EventID.TextEnd],
  },
  {
    headers: [EventID.ButtonDif],
    options: [EventID.ButtonDifChoose],
    ends: [EventID.ButtonDifEnd],
  },
  {
    headers: [EventID.IF, EventID.SeniorIf],
    options: [EventID.IfChoice],
    ends: [EventID.IFEnd],
  },
  {
    headers: [EventID.Loop],
    options: [],
    ends: [EventID.LoopAboveStart],
  },
]

export type StructureSelectionContext =
  | { kind: 'none' }
  | { kind: 'header'; headerIndex: number; endIndex: number; rule: StructureRule }
  | { kind: 'option'; headerIndex: number; optionIndex: number; endIndex: number; rule: StructureRule }
  | { kind: 'end'; headerIndex: number; endIndex: number; rule: StructureRule }
  | { kind: 'branch'; headerIndex: number; optionIndex: number; endIndex: number; rule: StructureRule }

export type StructureBlockRange = {
  headerIndex: number
  endIndex: number
  endExclusive: number
  headerIndent: number
  optionIndent: number
  rule: StructureRule
}

function getHeaderRule(code: number): StructureRule | null {
  return STRUCTURE_RULES.find((rule) => rule.headers.includes(code as EventID)) ?? null
}

function isIndentedOptionHeaderCode(code: EventID): boolean {
  return (
    code === EventID.TextDif
    || code === EventID.TextDifEx
    || code === EventID.TextDifEx2
    || code === EventID.ButtonDif
  )
}

function getOptionIndent(rule: StructureRule, headerIndent: number): number {
  if (rule.headers.some(isIndentedOptionHeaderCode)) {
    return headerIndent + 1
  }
  if (rule.options.length > 0) {
    return headerIndent
  }
  return headerIndent + 1
}

function getIndent(event: EventBase | null | undefined): number {
  return Math.max(0, event?.Indent ?? 0)
}

export function isStructureHeaderCode(code: number): boolean {
  return STRUCTURE_RULES.some((rule) => rule.headers.includes(code as EventID))
}

export function isStructureOptionCode(code: number): boolean {
  return STRUCTURE_RULES.some((rule) => rule.options.includes(code as EventID))
}

export function isStructureEndCode(code: number): boolean {
  return STRUCTURE_RULES.some((rule) => rule.ends.includes(code as EventID))
}

export function isStructureReadonlyCode(code: number): boolean {
  return isStructureOptionCode(code) || isStructureEndCode(code)
}

/**
 * 事件块内是否存在未被同块「循环」头包裹的中断循环（粘贴时需依赖目标位置已在循环体内）。
 */
export function hasOrphanLoopBreak(events: EventBase[]): boolean {
  for (let index = 0; index < events.length; index += 1) {
    if ((events[index]?.Code ?? -1) !== EventID.LoopBreak) continue
    let covered = false
    for (let headerIndex = 0; headerIndex < index; headerIndex += 1) {
      if ((events[headerIndex]?.Code ?? -1) !== EventID.Loop) continue
      const block = getStructureBlockRange(events, headerIndex)
      if (block && index <= block.endIndex) {
        covered = true
        break
      }
    }
    if (!covered) return true
  }
  return false
}

export function isTextChoiceHeaderCode(code: number): boolean {
  return code === EventID.TextDif || code === EventID.TextDifEx || code === EventID.TextDifEx2
}

export function isButtonChoiceHeaderCode(code: number): boolean {
  return code === EventID.ButtonDif
}

export function isIfHeaderCode(code: number): boolean {
  return code === EventID.IF || code === EventID.SeniorIf
}

function isLoopHeaderCode(code: number): boolean {
  return code === EventID.Loop
}

function isIfOrLoopHeaderCode(code: number): boolean {
  return isIfHeaderCode(code) || isLoopHeaderCode(code)
}

/** 结构块选中范围：结构头整块；选项/结束/分支内普通事件单行。 */
function resolveStructureBlockSelectionRange(
  structureHeaderCode: number,
  context: Exclude<StructureSelectionContext, { kind: 'none' }>,
  index: number,
): { start: number; end: number } {
  if (isTextChoiceHeaderCode(structureHeaderCode) || isButtonChoiceHeaderCode(structureHeaderCode)) {
    if (context.kind === 'option' || context.kind === 'end' || context.kind === 'branch') {
      return { start: index, end: index }
    }
    return { start: context.headerIndex, end: context.endIndex }
  }
  if (isIfOrLoopHeaderCode(structureHeaderCode)) {
    if (context.kind === 'branch') {
      return { start: index, end: index }
    }
    return { start: context.headerIndex, end: context.endIndex }
  }
  if (context.kind === 'header') {
    return { start: context.headerIndex, end: context.endIndex }
  }
  return { start: index, end: index }
}

function isTextOrButtonChoiceHeaderCode(code: number): boolean {
  return isTextChoiceHeaderCode(code) || isButtonChoiceHeaderCode(code)
}

export function isChoiceBlockHeaderCode(code: number): boolean {
  return isTextChoiceHeaderCode(code) || isButtonChoiceHeaderCode(code) || isIfHeaderCode(code)
}

function isLegacyFlatOption(rule: StructureRule, event: EventBase | null | undefined, indent: number, headerIndent: number): boolean {
  return (
    indent === headerIndent
    && rule.headers.some(isIndentedOptionHeaderCode)
    && rule.options.includes((event?.Code ?? -1) as EventID)
  )
}

function isLoopRule(rule: StructureRule): boolean {
  return rule.headers.length === 1 && rule.headers[0] === EventID.Loop
}

/**
 * 循环块边界：从循环头扫到同级「以上反复」为止，中间事件均算块内（不因 Indent 误判截断）。
 */
function resolveLoopStructureBlockRange(
  events: EventBase[],
  startIdx: number,
  headerIndent: number,
  optionIndent: number,
  rule: StructureRule,
): StructureBlockRange {
  for (let index = startIdx + 1; index < events.length; index += 1) {
    const event = events[index]
    if (getIndent(event) === headerIndent && (event?.Code ?? -1) === EventID.LoopAboveStart) {
      return {
        headerIndex: startIdx,
        endIndex: index,
        endExclusive: index + 1,
        headerIndent,
        optionIndent,
        rule,
      }
    }
  }
  return {
    headerIndex: startIdx,
    endIndex: events.length - 1,
    endExclusive: events.length,
    headerIndent,
    optionIndent,
    rule,
  }
}

export function getStructureBlockRange(events: EventBase[], startIdx: number): StructureBlockRange | null {
  if (startIdx < 0 || startIdx >= events.length) return null
  const header = events[startIdx]
  const rule = getHeaderRule(header?.Code ?? -1)
  if (!header || !rule) return null
  const headerIndent = getIndent(header)
  const optionIndent = getOptionIndent(rule, headerIndent)
  if (isLoopRule(rule)) {
    return resolveLoopStructureBlockRange(events, startIdx, headerIndent, optionIndent, rule)
  }
  for (let index = startIdx + 1; index < events.length; index += 1) {
    const event = events[index]
    const indent = getIndent(event)
    if (indent > headerIndent) continue
    if (isLegacyFlatOption(rule, event, indent, headerIndent)) {
      continue
    }
    if (indent === optionIndent && rule.options.includes((event?.Code ?? -1) as EventID)) {
      continue
    }
    if (indent === headerIndent && rule.ends.includes((event?.Code ?? -1) as EventID)) {
      return {
        headerIndex: startIdx,
        endIndex: index,
        endExclusive: index + 1,
        headerIndent,
        optionIndent,
        rule,
      }
    }
    return {
      headerIndex: startIdx,
      endIndex: index - 1,
      endExclusive: index,
      headerIndent,
      optionIndent,
      rule,
    }
  }
  return {
    headerIndex: startIdx,
    endIndex: events.length - 1,
    endExclusive: events.length,
    headerIndent,
    optionIndent,
    rule,
  }
}

export function findStructureBlockEndIndex(events: EventBase[], startIdx: number): number {
  return getStructureBlockRange(events, startIdx)?.endIndex ?? startIdx
}

/**
 * 将连续下标区间扩展到所有触及的结构块完整范围，避免跨块选中只覆盖半个结构块。
 */
export function expandStructureBlocksInIndexRange(
  events: EventBase[],
  start: number,
  end: number,
): { start: number; end: number } {
  if (start < 0 || end < start) return { start, end }
  let nextStart = start
  let nextEnd = end
  for (let index = start; index <= end; index += 1) {
    const context = getStructureSelectionContext(events, index)
    if (context.kind === 'none') continue
    nextStart = Math.min(nextStart, context.headerIndex)
    nextEnd = Math.max(nextEnd, context.endIndex)
  }
  return { start: nextStart, end: nextEnd }
}

/**
 * 当前主选中行是否为单行选中（用于列表高亮，避免分支内事件误显示为整块选中）。
 */
export function isSingleLineEventSelection(events: EventBase[], index: number): boolean {
  if (index < 0 || index >= events.length) return false
  const range = getEventSelectionRange(events, index)
  return range.start === range.end
}

function resolveFoldBlockSelectionRange(
  events: EventBase[],
  index: number,
): { start: number; end: number } {
  for (let cursor = index; cursor >= 0; cursor -= 1) {
    const code = events[cursor]?.Code ?? -1
    if (!isFoldBlockHeader(code)) continue
    const end = findStructureBlockEndIndex(events, cursor)
    if (cursor <= index && index <= end) {
      if (isIfOrLoopHeaderCode(code)) {
        return { start: index, end: index }
      }
      return { start: cursor, end }
    }
  }
  return { start: index, end: index }
}

/**
 * 计算点击某行后应选中的连续下标区间。
 */
export function getEventSelectionRange(
  events: EventBase[],
  index: number,
): { start: number; end: number } {
  if (index < 0 || index >= events.length) return { start: -1, end: -1 }
  const structureContext = getStructureSelectionContext(events, index)
  if (structureContext.kind === 'none') {
    return resolveFoldBlockSelectionRange(events, index)
  }
  const structureHeaderCode = events[structureContext.headerIndex]?.Code ?? -1
  return resolveStructureBlockSelectionRange(structureHeaderCode, structureContext, index)
}

function findIndentedBlockEndIndex(events: EventBase[], startIdx: number): number {
  if (startIdx < 0 || startIdx >= events.length) return startIdx
  const baseIndent = getIndent(events[startIdx])
  for (let index = startIdx + 1; index < events.length; index += 1) {
    const indent = getIndent(events[index])
    if (indent <= baseIndent) return index - 1
  }
  return events.length - 1
}

export function findFoldableEndIndex(events: EventBase[], startIdx: number): number {
  if (startIdx < 0 || startIdx >= events.length) return startIdx
  if (isStructureHeaderCode(events[startIdx]?.Code ?? -1)) {
    return findStructureBlockEndIndex(events, startIdx)
  }
  return findIndentedBlockEndIndex(events, startIdx)
}

export function getStructureSelectionContext(
  events: EventBase[],
  index: number,
): StructureSelectionContext {
  if (index < 0 || index >= events.length) return { kind: 'none' }
  const current = events[index]
  if (!current) return { kind: 'none' }

  for (let cursor = index; cursor >= 0; cursor -= 1) {
    const header = events[cursor]
    const range = getStructureBlockRange(events, cursor)
    if (!header || !range) continue
    const { rule, endIndex, headerIndent, optionIndent } = range
    if (index > endIndex) continue
    const currentIndent = getIndent(current)

    if (index === cursor) {
      return { kind: 'header', headerIndex: cursor, endIndex, rule }
    }
    if (currentIndent === headerIndent && rule.ends.includes((current.Code ?? -1) as EventID)) {
      return { kind: 'end', headerIndex: cursor, endIndex, rule }
    }
    if (
      (
        currentIndent === optionIndent
        || isLegacyFlatOption(rule, current, currentIndent, headerIndent)
      )
      && rule.options.includes((current.Code ?? -1) as EventID)
    ) {
      return { kind: 'option', headerIndex: cursor, optionIndex: index, endIndex, rule }
    }

    for (let optionIndex = cursor + 1; optionIndex <= endIndex; optionIndex += 1) {
      const optionEvent = events[optionIndex]
      const optionEventIndent = getIndent(optionEvent)
      if (
        !optionEvent
        || (
          optionEventIndent !== optionIndent
          && !isLegacyFlatOption(rule, optionEvent, optionEventIndent, headerIndent)
        )
        || !rule.options.includes((optionEvent.Code ?? -1) as EventID)
      ) {
        continue
      }
      let branchEnd = endIndex
      for (let nextIndex = optionIndex + 1; nextIndex <= endIndex; nextIndex += 1) {
        const nextEvent = events[nextIndex]
        if (!nextEvent) continue
        const nextIndent = getIndent(nextEvent)
        if (
          (
            (nextIndent === optionIndent || isLegacyFlatOption(rule, nextEvent, nextIndent, headerIndent))
            && rule.options.includes((nextEvent.Code ?? -1) as EventID)
          )
          || (nextIndent === headerIndent && rule.ends.includes((nextEvent.Code ?? -1) as EventID))
        ) {
          branchEnd = nextIndex - 1
          break
        }
      }
      if (index > optionIndex && index <= branchEnd) {
        return { kind: 'branch', headerIndex: cursor, optionIndex, endIndex, rule }
      }
    }

    if (index > cursor && index <= endIndex) {
      return { kind: 'branch', headerIndex: cursor, optionIndex: cursor, endIndex, rule }
    }

    return { kind: 'none' }
  }

  return { kind: 'none' }
}

export function isChoiceBlockDropTargetAllowed(
  events: EventBase[],
  targetIndex: number,
  position: 'before' | 'after',
): boolean {
  const context = getStructureSelectionContext(events, targetIndex)
  if (context.kind === 'none') return true
  const headerCode = events[context.headerIndex]?.Code ?? -1
  if (isIfOrLoopHeaderCode(headerCode)) {
    if (context.kind === 'branch' || context.kind === 'header') return true
    if (context.kind === 'option' || context.kind === 'end') return position === 'after'
    return true
  }
  if (!isTextOrButtonChoiceHeaderCode(headerCode)) return true
  if (context.kind === 'header') return position === 'before'
  if (context.kind === 'option' || context.kind === 'end') return position === 'after'
  return true
}

export function rebuildFoldIndent(events: EventBase[]): Set<number> {
  const collapsed = new Set<number>()
  const activeEnds: number[] = []
  for (let index = 0; index < events.length; index += 1) {
    while (activeEnds.length && index > activeEnds[activeEnds.length - 1]!) {
      activeEnds.pop()
    }
    const event = events[index]
    if (!event) continue
    event.FoldIndent = activeEnds.length
    if (Number(event.isFold) > 0) {
      const endIndex = findFoldableEndIndex(events, index)
      if (endIndex > index) {
        collapsed.add(index)
        activeEnds.push(endIndex)
      }
    }
  }
  return collapsed
}
